import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { matchPath, useLocation, useParams } from "react-router-dom";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Collapse, Container, Stack } from "@mui/material";
import {
  AsyncContentMui,
  // DataBox,
  StatusComponentGuard
} from "components";
import { CharacteristicsBar, DealDrawer, DealHierarchyPanel, DealManage, NavBarHorizontal } from "components/deal";
import { DealRoleGuard } from "components/guards";
import { LeftSlider } from "components/modals";
import { LetsTalkButton } from "components/Opportunities/Lets-talk-button/LetsTalkButton";
import { Routes } from "constants/routes";
import { useActionHook, useDealSocketHook, useNavigate, useNavigationHooks } from "hooks";
import { DealRouter } from "Routers/Deal-routes/DealRoutes";
import {
  useCharacteristicManageData,
  useDealActions,
  useDealTitleSelector,
  useIsDealCharBarOpenSelect,
  useOpportunityIdSelector,
  useUserActions,
  useViewActions
} from "store";
import { RoutesValues } from "store/types";
import { delay } from "utils/delay";

import {
  MISSING_FIELD_IDS_KEY,
  MISSING_FIELD_NAMES_KEY,
  REQUIRED_FOR_STAGE_KEY,
  REVIEW_TRIGGER_KEY,
  RoleName,
  TARGET_STAGE_NAME_KEY
} from "../../constants";
import { useDealRedirect } from "./use-deal-redirect";

// const noDataboxRoutes: RoutesValues[] = [
//   Routes.dealMonitoringData,
//   Routes.dealMonitoringDrafts,
//   Routes.dealMonitoring,
//   Routes.dealTaskManage,
//   Routes.dealReport,
//   Routes.dealNotes,
//   Routes.dealDashboard,
//   Routes.dealLookThrough,
//   Routes.dealAmortization
// ];

const openCharBarRoutes: RoutesValues[] = [Routes.dealDashboard, Routes.dealMonitoring, Routes.dealReport];

export const Deal: React.FC = () => {
  const isCharBarOpen = useIsDealCharBarOpenSelect();
  const [isLoading, setIsLoading] = useState<boolean>(true); // Initial loading is true so inner Default-View won't mount twice

  const { pathname, search } = useLocation();
  const { deal_id } = useParams<{ deal_id: string }>();
  const searchParams = new URLSearchParams(search);
  const hasRequiredFieldsParams = searchParams.has(REQUIRED_FOR_STAGE_KEY) && searchParams.has(MISSING_FIELD_IDS_KEY);
  const requiredForStageId = searchParams.get(REQUIRED_FOR_STAGE_KEY) ?? undefined;
  const targetStageName = searchParams.get(TARGET_STAGE_NAME_KEY) ?? undefined;
  const missingFieldNames = useMemo(() => {
    const names = searchParams.get(MISSING_FIELD_NAMES_KEY);
    if (!names) return [];
    return names
      .split(",")
      .map((n) => n.trim())
      .filter(Boolean);
  }, [search]);
  const reviewTrigger = searchParams.get(REVIEW_TRIGGER_KEY) ?? undefined;

  const { subscribe } = useDealSocketHook();
  const navigate = useNavigate();
  const { removeParams } = useNavigationHooks();
  const listenRedirect = useDealRedirect();
  const opportunityId = useOpportunityIdSelector();
  const lastOpenedDrawerForParamsRef = useRef<string>("");

  const { setFilterByDeal, getNotificationsAction } = useUserActions();
  const { setDealRedirectAction, setIsDealCharBarOpenAction } = useViewActions();
  const {
    dealInvalidateAction,
    getDealAndStoreAction,
    getDealChunksAction,
    getDealCharacteristicsAction,
    getDealRoundsWithInvestorsAndStoreAction,
    setCharacteristicsManageAction
  } = useDealActions();
  const dealTitle = useDealTitleSelector();
  const characteristicsManage = useCharacteristicManageData();

  const { action: getDealCharacteristics, data } = useActionHook(getDealCharacteristicsAction);

  // const isDataboxHidden: boolean = useMemo(
  //   () => noDataboxRoutes.some((route) => matchPath(pathname, route)),
  //   [pathname]
  // );

  const onBackButtonEvent = (e: any) => {
    e.preventDefault();
    navigate({ route: "myDeals" });
  };

  const refetchCharacteristicList = useCallback(async (dealId: number) => {
    try {
      await getDealCharacteristics(dealId);
    } catch (err) {
      console.log(err);
    }
  }, []);

  const refetchCharacteristicParts = useCallback(async (dealId: number) => {
    try {
      await getDealAndStoreAction(dealId);
      await refetchCharacteristicList(dealId);
    } catch (err) {
      console.log(err);
    }
  }, []);

  const fetchDeal = useCallback(async (dealId: number) => {
    try {
      dealInvalidateAction();
      listenRedirect(false);
      setIsLoading(true);
      await getDealAndStoreAction(dealId);
      await getDealChunksAction(dealId);
      await getDealCharacteristics(dealId);
      await getDealRoundsWithInvestorsAndStoreAction(dealId);
      await delay(100); // Delay to let dispatch deliver workspace content before redirect on
      // Finally subscribe to deal web sockets
      subscribe();
      listenRedirect(true);
    } catch (_err) {
      navigate({ route: "myDeals" });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!deal_id) return;
    fetchDeal(Number(deal_id));
  }, [deal_id]);

  const isDefaultOpenCharBarRoute = useMemo(() => {
    return openCharBarRoutes.some((route) => matchPath(pathname, route));
  }, [pathname]);

  useEffect(() => {
    if (!isDefaultOpenCharBarRoute && isCharBarOpen && !hasRequiredFieldsParams) {
      setIsDealCharBarOpenAction(false);
    }
  }, [isDefaultOpenCharBarRoute, hasRequiredFieldsParams]);

  useEffect(() => {
    if (!hasRequiredFieldsParams) {
      lastOpenedDrawerForParamsRef.current = "";
    }
  }, [hasRequiredFieldsParams]);

  // When the drawer was open for "Review missing fields" and user closed it, clear the URL params.
  // Do not clear on initial load (we have params but haven't opened the drawer yet); only clear after we've opened the drawer (lastOpenedDrawerForParamsRef set) and then it closes.
  useEffect(() => {
    if (characteristicsManage === null && hasRequiredFieldsParams && lastOpenedDrawerForParamsRef.current !== "") {
      removeParams([
        REQUIRED_FOR_STAGE_KEY,
        MISSING_FIELD_IDS_KEY,
        TARGET_STAGE_NAME_KEY,
        MISSING_FIELD_NAMES_KEY,
        REVIEW_TRIGGER_KEY
      ]);
      lastOpenedDrawerForParamsRef.current = "";
    }
  }, [characteristicsManage, hasRequiredFieldsParams, removeParams]);

  useEffect(() => {
    if (!hasRequiredFieldsParams || !deal_id || !dealTitle) return;
    const paramKey = `${requiredForStageId ?? ""}-${missingFieldNames.join(",")}-${reviewTrigger ?? ""}`;
    if (lastOpenedDrawerForParamsRef.current === paramKey) return;
    lastOpenedDrawerForParamsRef.current = paramKey;
    setCharacteristicsManageAction({
      dealId: Number(deal_id),
      dealName: dealTitle,
      targetStageName: targetStageName ?? undefined,
      requiredForStageId: requiredForStageId ?? undefined,
      missingFieldNames: missingFieldNames.length ? missingFieldNames : undefined,
      onSubmitSuccess: () => navigate({ route: "deal", params: { deal_id }, search: {} })
    });
  }, [
    hasRequiredFieldsParams,
    deal_id,
    dealTitle,
    targetStageName,
    requiredForStageId,
    missingFieldNames,
    reviewTrigger,
    setCharacteristicsManageAction,
    navigate
  ]);

  useEffect(() => {
    window.addEventListener("popstate", onBackButtonEvent);
    // Unmount
    return () => {
      window.removeEventListener("popstate", onBackButtonEvent);
      setDealRedirectAction(null);
      dealInvalidateAction();
      setFilterByDeal(false);
      getNotificationsAction(1);
    };
  }, []);

  const whiteBackground = useMemo(() => {
    return (
      !matchPath(pathname, Routes.dealDashboard) &&
      !matchPath(pathname, Routes.dealReport) &&
      !matchPath(pathname, Routes.dealDebtReport) &&
      !matchPath(pathname, Routes.dealAmortization)
    );
  }, [pathname]);

  const showBoxShadow = useMemo(() => {
    return (
      !matchPath(pathname, Routes.dealDashboard) &&
      !matchPath(pathname, Routes.dealLookThrough) &&
      !matchPath(pathname, Routes.dealInsights) &&
      !matchPath(pathname, Routes.dealWorkspace) &&
      !matchPath(pathname, Routes.dealReport) &&
      !matchPath(pathname, Routes.dealAmortization)
    );
  }, [pathname]);

  return (
    <>
      <AsyncContentMui isLoading={isLoading} spinnerProps={{ size: 200 }}>
        <Stack
          alignItems="stretch"
          gap="10px"
          width="100%"
          height="100%"
          overflow="hidden"
          data-testid="deal-page"
          className="deal"
        >
          <Stack
            sx={{ position: "relative" }}
            direction="row"
            useFlexGap
            justifyContent="space-between"
            alignItems="center"
          >
            <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}>
              <NavBarHorizontal />
            </DealRoleGuard>
            <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}>
              <DealManage />
            </DealRoleGuard>
            <StatusComponentGuard statuses={[opportunityId]} show={false}>
              <MoreHorizIcon
                sx={{
                  backgroundColor: "#ffffff",
                  cursor: "pointer",
                  width: "35px",
                  height: "20px",
                  borderRadius: "7px",
                  border: "0.5px solid #dae0e2",
                  position: "absolute",
                  left: "-6px",
                  bottom: "-20px",
                  right: "0",
                  marginLeft: "auto",
                  marginRight: "auto",
                  zIndex: "10"
                }}
                onClick={() => setIsDealCharBarOpenAction(!isCharBarOpen)}
              />
            </StatusComponentGuard>
          </Stack>
          <Stack sx={{ alignItems: "stretch", flexGrow: 1, overflow: "hidden", position: "relative" }}>
            <Container
              sx={{
                p: "0 !important",
                ...(!isCharBarOpen && { borderTop: "1px solid #ced6d8", marginBottom: "16px" })
              }}
              maxWidth={false}
            >
              <Collapse in={isCharBarOpen} timeout={500} unmountOnExit>
                {data && (
                  <CharacteristicsBar
                    list={data}
                    refetch={() => refetchCharacteristicParts(Number(deal_id))}
                    missingFieldNames={hasRequiredFieldsParams ? missingFieldNames : undefined}
                  />
                )}
              </Collapse>
            </Container>
            <Stack direction="row" flexGrow={1} position="relative" overflow="hidden">
              {/* {!isDataboxHidden && <DataBox />}*/}
              <Stack
                sx={{
                  overflow: "hidden",
                  position: "relative",
                  width: "100%",
                  borderRadius: "8px",
                  ...(showBoxShadow && { boxShadow: "0 1px 2px 0 #1212171a", margin: "0 2px 2px 2px" }),
                  ...(whiteBackground && { backgroundColor: "#fff" }),
                  ...(matchPath(pathname, Routes.dealDataRoom) && { width: "auto", flexGrow: 1 }) // Make container wider when dealDataroom
                }}
              >
                <DealRouter />
              </Stack>
              {/* <DealWorkspace /> */}
              <LetsTalkButton />
            </Stack>
            <DealDrawer />
          </Stack>
        </Stack>
      </AsyncContentMui>
      <LeftSlider />
      <DealHierarchyPanel />
    </>
  );
};
